import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { getSettings, updateSettings, useSettings } from '../api/settings'
import { listTeam, sendTestAlert, downloadWeeklyReportPreview,
  listWebhooks, createWebhook, updateWebhook, deleteWebhook, testWebhook, listWebhookDeliveries } from '../api/tankguard'
import { useToast } from '../components/Toast'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import { getUser } from '../api/auth'

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
    </svg>
  )
}

function OrgIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3M9 9v.01M9 13v.01M9 17v.01" />
    </svg>
  )
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  )
}

function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M3 21v-1a6 6 0 0 1 12 0v1M16 3.5a4 4 0 0 1 0 7.7M21 21v-1a6 6 0 0 0-4-5.6" />
    </svg>
  )
}

function WebhookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 11.5l4-6M14.5 13.5l3.5 6M5 19a3 3 0 1 0 3-3M16 5a3 3 0 1 1 3 3M8 19a3 3 0 0 1 3-3M16 5l-2.5 4.5" />
    </svg>
  )
}

function DevicesIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 9h6v6H9zM9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5 10-10" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" />
    </svg>
  )
}

function Toggle({ checked, onChange }) {
  return (
    <label className="switch">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="track" />
      <span className="knob" />
    </label>
  )
}

const MEMBER_COLORS = [
  'var(--tg-teal-700)', 'var(--tg-graphite-600)', '#7c5cbf', '#b45309', '#0e7490',
]
function initials(name) {
  return (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

function relativeLastSeen(iso) {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60000) return 'now'
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago'
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago'
  return Math.floor(diff / 86400000) + 'd ago'
}

// `labelKey` points at the i18n key; resolved with t() at render time so the
// nav follows the active language.
const SECTIONS = [
  { key: 'profile',  labelKey: 'settings.profile',             Icon: ProfileIcon },
  { key: 'org',      labelKey: 'settings.organization',        Icon: OrgIcon },
  { key: 'alerts',   labelKey: 'settings.alertsNotifications', Icon: AlertIcon },
  { key: 'webhooks', labelKey: 'settings.webhooks',            Icon: WebhookIcon },
  { key: 'team',     labelKey: 'settings.team',                Icon: TeamIcon },
  { key: 'devices',  labelKey: 'settings.devices',             Icon: DevicesIcon },
]

const WEBHOOK_EVENTS = [
  { value: 'theft',            label: 'Theft' },
  { value: 'tamper',           label: 'Sensor tamper' },
  { value: 'refuel',           label: 'Refuel' },
  { value: 'geofence',         label: 'Geofence breach' },
  { value: 'after_hours',      label: 'After-hours movement' },
  { value: 'anomaly',          label: 'Consumption anomaly' },
  { value: 'correlated_theft', label: 'Coordinated theft' },
]

function validateKey(key, value) {
  const v = String(value).trim()
  if (key === 'diesel_price_eur') {
    const n = parseFloat(v)
    if (isNaN(n) || n < 0.1 || n > 10) return 'Must be 0.10–10.00'
  }
  if (key === 'theft_threshold_l' || key === 'refuel_threshold_l' || key === 'low_fuel_threshold_l') {
    const n = parseFloat(v)
    if (isNaN(n) || n <= 0) return 'Must be positive'
  }
  if (key === 'refresh_interval_s') {
    const n = parseInt(v)
    if (isNaN(n) || n < 5 || n > 300) return 'Must be 5–300'
  }
  if (key === 'operating_hours_start' || key === 'operating_hours_end') {
    if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(v)) return 'Must be HH:MM'
  }
  return ''
}

// Toggle one ISO weekday in/out of the comma-separated operating_days string.
function toggleDay(current, day) {
  const set = new Set(String(current || '').split(',').map(s => parseInt(s.trim())).filter(n => n >= 1 && n <= 7))
  if (set.has(day)) set.delete(day); else set.add(day)
  return Array.from(set).sort().join(',')
}

const DAY_LABELS = [
  { id: 1, label: 'Mon' }, { id: 2, label: 'Tue' }, { id: 3, label: 'Wed' },
  { id: 4, label: 'Thu' }, { id: 5, label: 'Fri' }, { id: 6, label: 'Sat' }, { id: 7, label: 'Sun' },
]

function PreviewWeeklyButton() {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  async function run() {
    setBusy(true)
    try { await downloadWeeklyReportPreview(); toast.success('Weekly report PDF downloaded') }
    catch (e) { toast.error('Preview failed', e.message); Sentry.captureException(e) }
    setBusy(false)
  }
  return (
    <Button variant="secondary" size="sm" onClick={run} loading={busy} disabled={busy}>
      Preview weekly report
    </Button>
  )
}

function TestAlertButton() {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  async function run() {
    setBusy(true)
    try {
      const r = await sendTestAlert()
      // r.results is { email, sms, whatsapp } — surface non-disabled outcomes.
      const sent = Object.entries(r.results || {})
        .filter(([, v]) => v === 'sent')
        .map(([k]) => k.toUpperCase())
      if (sent.length) toast.success('Test alert sent', `${sent.join(' + ')} dispatched. Check the inbox / phone.`)
      else toast.error('No channel fired — check toggles + numbers', JSON.stringify(r.results))
    } catch (e) { toast.error('Test failed', e.message) }
    setBusy(false)
  }
  return (
    <Button variant="secondary" size="sm" onClick={run} loading={busy} disabled={busy}>
      Send test alert
    </Button>
  )
}

function WebhookEditor({ initial, onSave, onCancel, busy }) {
  const [name, setName] = useState(initial?.name || '')
  const [url, setUrl] = useState(initial?.url || '')
  const [secret, setSecret] = useState('')
  const [events, setEvents] = useState(initial?.events || ['theft'])

  function toggleEvent(v) {
    setEvents(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])
  }

  const canSave = name.trim().length >= 2 && /^https?:\/\//.test(url) && events.length > 0 && !busy

  return (
    <div style={{ padding: 'var(--tg-space-4)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)', background: 'var(--tg-bg-surface)' }}>
      <div className="frow">
        <div className="tg-field">
          <label className="tg-label">Name</label>
          <input className="tg-input" value={name} onChange={e => setName(e.target.value)} placeholder="Slack #ops" />
        </div>
        <div className="tg-field">
          <label className="tg-label">URL</label>
          <input className="tg-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://hooks.example.com/abc" style={{ fontFamily: 'var(--tg-font-mono)' }} />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="tg-label">{initial ? 'Replace secret (optional)' : 'HMAC secret (optional)'}</label>
        <input className="tg-input" type="password" value={secret} onChange={e => setSecret(e.target.value)}
          placeholder={initial?.secret_set ? 'Secret already set — leave blank to keep, type to replace, set "-" to clear' : 'Used to sign X-Tankguard-Signature'}
          style={{ fontFamily: 'var(--tg-font-mono)' }} />
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="tg-label" style={{ display: 'block', marginBottom: 8 }}>Events</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {WEBHOOK_EVENTS.map(e => {
            const active = events.includes(e.value)
            return (
              <button key={e.value} type="button" onClick={() => toggleEvent(e.value)}
                style={{
                  padding: '6px 12px', borderRadius: 'var(--tg-radius-md)',
                  border: '1px solid ' + (active ? 'var(--tg-teal-500)' : 'var(--tg-border-default)'),
                  background: active ? 'var(--tg-teal-50, #ecfdf5)' : 'var(--tg-bg-default, #fff)',
                  color: active ? 'var(--tg-teal-700)' : 'var(--tg-text-secondary)',
                  fontSize: 'var(--tg-text-sm)', fontWeight: active ? 600 : 500, cursor: 'pointer',
                }}>
                {e.label}
              </button>
            )
          })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => {
          // Empty secret string left untouched; "-" sentinel clears the secret.
          const sec = secret === '' ? undefined : (secret === '-' ? '' : secret)
          onSave({ name: name.trim(), url, secret: sec, events })
        }} disabled={!canSave} loading={busy}>
          {initial ? 'Save changes' : 'Create webhook'}
        </Button>
      </div>
    </div>
  )
}

function WebhookRow({ hook, onEdit, onDelete, onTest, onToggle }) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  async function fireTest() {
    setBusy(true)
    try {
      const r = await onTest(hook.id)
      if (r.ok) toast.success('Test fired', `HTTP ${r.status} · ${r.response_ms} ms`)
      else toast.error('Test failed', r.error || `HTTP ${r.status}`)
    } catch (e) { toast.error('Test failed', e.message) }
    setBusy(false)
  }
  const statusColor = !hook.active ? 'var(--tg-text-muted)'
    : hook.last_status_code && hook.last_status_code >= 200 && hook.last_status_code < 300 ? 'var(--tg-status-online)'
    : hook.last_status_code ? 'var(--tg-danger-700)'
    : 'var(--tg-text-tertiary)'
  return (
    <div style={{ padding: 'var(--tg-space-4)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, color: 'var(--tg-text-primary)' }}>
            {hook.name}
            {!hook.active && <span style={{ marginLeft: 8, fontSize: 11, padding: '1px 8px', background: 'var(--tg-graphite-100, #f3f4f6)', color: 'var(--tg-text-muted)', borderRadius: 4 }}>disabled</span>}
          </div>
          <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', fontFamily: 'var(--tg-font-mono)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hook.url}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {(hook.events || []).map(e => (
              <span key={e} style={{ fontSize: 11, padding: '2px 8px', background: 'var(--tg-graphite-100, #f3f4f6)', color: 'var(--tg-text-secondary)', borderRadius: 4 }}>{e}</span>
            ))}
          </div>
          <div style={{ fontSize: 'var(--tg-text-xs)', color: statusColor, marginTop: 8, fontVariantNumeric: 'tabular-nums' }}>
            {hook.last_fired_at
              ? `Last fired ${relativeLastSeen(hook.last_fired_at)} · HTTP ${hook.last_status_code ?? '—'}`
              : 'Never fired'}
            {hook.fail_count > 0 && ` · ${hook.fail_count} failed`}
            {hook.secret_set && ' · HMAC signed'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="ghost" size="sm" onClick={fireTest} loading={busy} disabled={busy}>Test</Button>
          <Button variant="secondary" size="sm" onClick={() => onToggle(hook)} disabled={busy}>
            {hook.active ? 'Disable' : 'Enable'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => onEdit(hook)} disabled={busy}>Edit</Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(hook)} disabled={busy}>Delete</Button>
        </div>
      </div>
    </div>
  )
}

function WebhooksSection() {
  const toast = useToast()
  const [hooks, setHooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [editing, setEditing] = useState(null)  // hook row when editing existing
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)

  async function load() {
    setLoading(true); setErr('')
    try { setHooks(await listWebhooks() || []) }
    catch (e) { setErr(e.message); Sentry.captureException(e) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  async function save(values) {
    setBusy(true)
    try {
      if (editing) {
        await updateWebhook(editing.id, values)
        toast.success('Webhook updated')
      } else {
        await createWebhook(values)
        toast.success('Webhook created')
      }
      setEditing(null); setCreating(false)
      await load()
    } catch (e) { toast.error('Save failed', e.message); Sentry.captureException(e) }
    setBusy(false)
  }

  async function remove(hook) {
    if (!confirm(`Delete webhook "${hook.name}"? This cannot be undone.`)) return
    setBusy(true)
    try { await deleteWebhook(hook.id); toast.success('Webhook deleted'); await load() }
    catch (e) { toast.error('Delete failed', e.message); Sentry.captureException(e) }
    setBusy(false)
  }

  async function toggle(hook) {
    setBusy(true)
    try { await updateWebhook(hook.id, { active: !hook.active }); await load() }
    catch (e) { toast.error('Toggle failed', e.message) }
    setBusy(false)
  }

  return (
    <div className="scard">
      <div className="scard__head">
        <h2>Webhooks</h2>
        <p>POST every fleet event to your ERP, dispatch system, or Slack. Each delivery is signed with HMAC-SHA256 so your receiver can verify it really came from Tankguard.</p>
      </div>
      <div className="scard__body">
        {loading && <div style={{ color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)' }}>Loading webhooks…</div>}
        {!loading && err && <div style={{ color: 'var(--tg-danger-700)', fontSize: 'var(--tg-text-sm)' }}>Couldn't load webhooks: {err}</div>}
        {!loading && !err && (
          <>
            {hooks.length === 0 && !creating && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)' }}>
                No webhooks configured yet. Add one to forward events to your stack.
              </div>
            )}
            {hooks.map(h => (
              editing?.id === h.id
                ? <WebhookEditor key={h.id} initial={h} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
                : <WebhookRow key={h.id} hook={h} onEdit={setEditing} onDelete={remove} onToggle={toggle} onTest={async id => testWebhook(id)} />
            ))}
            {creating && (
              <WebhookEditor onSave={save} onCancel={() => setCreating(false)} busy={busy} />
            )}
          </>
        )}
      </div>
      <div className="scard__foot">
        <span style={{ flex: 1, fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)' }}>
          Signature header: <code style={{ fontFamily: 'var(--tg-font-mono)' }}>X-Tankguard-Signature: sha256=&lt;hex&gt;</code> over the raw body.
        </span>
        <Button variant="primary" onClick={() => { setCreating(true); setEditing(null) }} disabled={creating}>
          Add webhook
        </Button>
      </div>
    </div>
  )
}

function TeamSection() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  useEffect(() => {
    let cancelled = false
    listTeam().then(rows => { if (!cancelled) { setMembers(rows || []); setLoading(false) } })
      .catch(e => { if (!cancelled) { setErr(e.message); setLoading(false); Sentry.captureException(e) } })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="scard">
      <div className="team-head">
        <h2>Team members</h2>
        <span style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)' }}>
          Team invitations are managed by your superadmin in <strong>Admin → Users</strong>.
        </span>
      </div>
      {loading && <div style={{ padding: 16, color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)' }}>Loading team…</div>}
      {!loading && err && <div style={{ padding: 16, color: 'var(--tg-danger-700)', fontSize: 'var(--tg-text-sm)' }}>Couldn't load team: {err}</div>}
      {!loading && !err && members.length === 0 && <div style={{ padding: 16, color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)' }}>No team members in this organization yet.</div>}
      {!loading && !err && members.length > 0 && (
        <table className="team">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Last active</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m, i) => (
              <tr key={m.id}>
                <td>
                  <div className="member">
                    <span className="av" style={{ background: MEMBER_COLORS[i % MEMBER_COLORS.length] }}>{initials(m.name || m.email)}</span>
                    <div>
                      <div className="mn">{m.name || m.email.split('@')[0]}</div>
                      <div className="me">{m.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', fontWeight: 'var(--tg-weight-medium)', textTransform: 'capitalize' }}>{m.role}</td>
                <td><Badge variant={m.last_login ? 'online' : 'offline'} size="sm" dot>{m.last_login ? 'Active' : 'Pending'}</Badge></td>
                <td style={{ textAlign: 'right', color: 'var(--tg-text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{relativeLastSeen(m.last_login)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ProfileSection() {
  const user = getUser()
  const toast = useToast()
  const { t } = useTranslation()
  const [name, setName] = useState(user?.display_name || user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [lang, setLang] = useState(user?.preferred_language || localStorage.getItem('tg_lang') || 'en')
  const [curPwd, setCurPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [saving, setSaving] = useState(false)

  async function saveProfile(e) {
    e.preventDefault()
    setSaving(true)
    try {
      const body = { name, email, preferred_language: lang }
      if (newPwd) { body.current_password = curPwd; body.new_password = newPwd }
      const res = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('tg_token') },
        body: JSON.stringify(body),
      })
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed') }
      const updated = await res.json()
      // Keep the cached session user in sync so the avatar, greeting, and the
      // language follow the change on the next render and after a refresh.
      try {
        const cached = JSON.parse(localStorage.getItem('tg_user')) || {}
        localStorage.setItem('tg_user', JSON.stringify({ ...cached, ...updated, org_name: cached.org_name }))
      } catch { /* non-fatal */ }
      localStorage.setItem('tg_lang', lang)
      window.dispatchEvent(new Event('tg_lang_change'))
      toast.success(t('settings.profileUpdated'))
      setCurPwd(''); setNewPwd('')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="scard">
      <div className="scard__head">
        <h2>{t('settings.profile')}</h2>
        <p>Your account details, language, and password.</p>
      </div>
      <form className="scard__body" onSubmit={saveProfile}>
        <Input label={t('settings.displayName')} value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
        <Input label={t('settings.emailAddress')} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />

        <div className="settings-section-title">{t('settings.language')}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[['en', '🇬🇧 English'], ['de', '🇩🇪 Deutsch']].map(([code, label]) => (
            <button key={code} type="button"
              onClick={() => setLang(code)}
              className={'tg-btn ' + (lang === code ? 'tg-btn--primary' : 'tg-btn--secondary')}
              style={{ minWidth: 120 }}>
              {label}
            </button>
          ))}
        </div>

        <div className="settings-section-title">{t('settings.changePassword')}</div>
        <Input label={t('settings.currentPassword')} type="password" value={curPwd} onChange={e => setCurPwd(e.target.value)} placeholder="Leave blank to keep current" autoComplete="current-password" />
        <Input label={t('settings.newPassword')} type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />

        <Button type="submit" variant="primary" loading={saving} style={{ marginTop: 8 }}>{t('settings.saveChanges')}</Button>
      </form>
    </div>
  )
}

export default function Settings() {
  const settings = useSettings()
  const toast = useToast()
  const { t } = useTranslation()
  const user = getUser()
  const isSuperadmin = user?.role === 'superadmin'

  const [section, setSection] = useState('org')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [edits, setEdits] = useState({})
  const [errs, setErrs] = useState({})

  // Notification toggles — persisted via settings keys notify_email_theft,
  // notify_push_theft, notify_sensor_health, notify_weekly_summary. Read from
  // the same settings rows; defaults applied when a key has not been written yet.
  const NOTIF_KEYS = ['email_theft', 'push_theft', 'sensor_health', 'weekly_summary']
  const NOTIF_DEFAULTS = { email_theft: true, push_theft: true, sensor_health: true, weekly_summary: false }
  function notifValue(uiKey) {
    const settingKey = 'notify_' + uiKey
    if (edits[settingKey] !== undefined) return edits[settingKey] === 'true' || edits[settingKey] === true
    const row = rows.find(r => r.key === settingKey)
    if (row) return row.value === 'true'
    return NOTIF_DEFAULTS[uiKey]
  }
  function setNotifValue(uiKey, on) {
    set('notify_' + uiKey, on ? 'true' : 'false')
  }

  async function load() {
    setLoading(true)
    try { setRows(await getSettings()) }
    catch (e) { toast.error(e.message); Sentry.captureException(e) }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  function val(key) {
    return edits[key] !== undefined ? edits[key] : (rows.find(r => r.key === key)?.value ?? '')
  }

  function set(key, value) {
    setEdits(e => ({ ...e, [key]: value }))
    setErrs(e => ({ ...e, [key]: validateKey(key, value) }))
  }

  function blur(key) {
    setErrs(e => ({ ...e, [key]: validateKey(key, val(key)) }))
  }

  const dirty = Object.keys(edits).length > 0
  const hasErrors = Object.values(errs).some(Boolean)

  async function save() {
    const newErrs = {}
    Object.keys(edits).forEach(k => { newErrs[k] = validateKey(k, edits[k]) })
    setErrs(e => ({ ...e, ...newErrs }))
    if (Object.values(newErrs).some(Boolean)) { toast.error('Fix validation errors before saving'); return }
    setSaving(true)
    try {
      if (Object.keys(edits).length > 0) await updateSettings(edits)
      setEdits({})
      await settings.reload()
      await load()
      toast.success('Settings saved')
    } catch (e) { toast.error(e.message); Sentry.captureException(e) }
    setSaving(false)
  }

  function discard() {
    setEdits({})
    setErrs({})
  }

  const orgId = user?.org_id ? 'org_' + String(user.org_id).slice(-6) : 'org_···'

  return (
    <div className="settings-page">
      <h1>Settings</h1>
      <div className="settings-grid">
        <nav className="settings-nav">
          {SECTIONS.map(({ key, labelKey, Icon }) => (
            <button
              key={key}
              className={section === key ? 'is-active' : ''}
              onClick={() => setSection(key)}
            >
              <Icon />
              {t(labelKey)}
            </button>
          ))}
        </nav>

        <div>
          {section === 'profile' && <ProfileSection />}

          {section === 'org' && (
            <>
              <div className="scard">
                <div className="scard__head">
                  <h2>Organization profile</h2>
                  <p>How your fleet appears across Tankguard. Scoped to this organization.</p>
                </div>
                <div className="scard__body">
                  <div className="frow">
                    <div className="tg-field">
                      <label className="tg-label">Organization name</label>
                      <input className="tg-input" value={val('company_name')} onChange={e => set('company_name', e.target.value)} onBlur={() => blur('company_name')} />
                    </div>
                    <div className="tg-field">
                      <label className="tg-label">Organization ID</label>
                      <div className="ro" style={{ fontFamily: 'var(--tg-font-mono)', fontSize: 'var(--tg-text-sm)' }}>{orgId} · read-only</div>
                    </div>
                  </div>
                  <div className="frow">
                    <div className="tg-field">
                      <label className="tg-label">Default fuel price (€/L)</label>
                      <input className="tg-input" type="number" step="0.01" min="0.1" max="10" value={val('diesel_price_eur')} onChange={e => set('diesel_price_eur', e.target.value)} onBlur={() => blur('diesel_price_eur')} style={{ fontFamily: 'var(--tg-font-mono)' }} />
                      {errs.diesel_price_eur && <div className="tg-field-error">{errs.diesel_price_eur}</div>}
                    </div>
                    <div className="tg-field">
                      <label className="tg-label">Currency</label>
                      <select className="tg-input" value={val('currency')} onChange={e => set('currency', e.target.value)} style={{ appearance: 'auto' }}>
                        <option value="EUR">EUR (€)</option>
                        <option value="USD">USD ($)</option>
                        <option value="GBP">GBP (£)</option>
                      </select>
                    </div>
                  </div>
                  <div className="frow">
                    <div className="tg-field">
                      <label className="tg-label">Data residency</label>
                      <div className="ro"><CheckIcon />Germany (Hetzner, Nuremberg)</div>
                    </div>
                    <div className="tg-field">
                      <label className="tg-label">Plan</label>
                      <div className="ro" style={{ color: 'var(--tg-text-secondary)' }}>Scale · up to 50 trucks</div>
                    </div>
                  </div>
                </div>
                <div className="scard__foot">
                  <Button variant="ghost" onClick={discard} disabled={!dirty}>Cancel</Button>
                  <Button variant="primary" onClick={save} loading={saving} disabled={saving || hasErrors || !dirty}>Save changes</Button>
                </div>
              </div>
            </>
          )}

          {section === 'alerts' && (
            <>
              <div className="scard">
                <div className="scard__head">
                  <h2>Theft detection</h2>
                  <p>When fuel drops while a truck is stationary, Tankguard raises a theft alert.</p>
                </div>
                <div className="scard__body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div>
                      <label className="tg-label" style={{ display: 'block', marginBottom: 8 }}>Drop threshold</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-4)' }}>
                        <input className="tg-input" type="number" min="1" max="500" value={val('theft_threshold_l')} onChange={e => set('theft_threshold_l', e.target.value)} onBlur={() => blur('theft_threshold_l')} style={{ width: 88, fontFamily: 'var(--tg-font-mono)' }} />
                        <span style={{ fontSize: 'var(--tg-text-base)', color: 'var(--tg-text-tertiary)' }}>liters or more while stationary triggers an alert</span>
                      </div>
                      {errs.theft_threshold_l && <div className="tg-field-error" style={{ marginTop: 6 }}>{errs.theft_threshold_l}</div>}
                      <div className="tg-help" style={{ marginTop: 8 }}>Lower catches smaller siphoning; higher reduces false positives. Default 10 L.</div>
                    </div>
                    <div>
                      <label className="tg-label" style={{ display: 'block', marginBottom: 8 }}>Refuel detection threshold</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-4)' }}>
                        <input className="tg-input" type="number" min="1" max="1000" value={val('refuel_threshold_l')} onChange={e => set('refuel_threshold_l', e.target.value)} onBlur={() => blur('refuel_threshold_l')} style={{ width: 88, fontFamily: 'var(--tg-font-mono)' }} />
                        <span style={{ fontSize: 'var(--tg-text-base)', color: 'var(--tg-text-tertiary)' }}>liters increase to classify as a refuel event</span>
                      </div>
                      {errs.refuel_threshold_l && <div className="tg-field-error" style={{ marginTop: 6 }}>{errs.refuel_threshold_l}</div>}
                    </div>
                    <div>
                      <label className="tg-label" style={{ display: 'block', marginBottom: 8 }}>Low fuel warning</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-4)' }}>
                        <input className="tg-input" type="number" min="1" max="2000" value={val('low_fuel_threshold_l')} onChange={e => set('low_fuel_threshold_l', e.target.value)} onBlur={() => blur('low_fuel_threshold_l')} style={{ width: 88, fontFamily: 'var(--tg-font-mono)' }} />
                        <span style={{ fontSize: 'var(--tg-text-base)', color: 'var(--tg-text-tertiary)' }}>liters remaining to show warning indicator</span>
                      </div>
                      {errs.low_fuel_threshold_l && <div className="tg-field-error" style={{ marginTop: 6 }}>{errs.low_fuel_threshold_l}</div>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="scard">
                <div className="scard__head">
                  <h2>Alert recipients</h2>
                  <p>Where to send theft alerts. Phone numbers in international E.164 format (e.g. +491701234567).</p>
                </div>
                <div className="scard__body">
                  <div className="frow">
                    <div className="tg-field">
                      <label className="tg-label">Alert email</label>
                      <input className="tg-input" type="email" value={val('alert_email')} onChange={e => set('alert_email', e.target.value)} placeholder="ops@yourfleet.de" />
                    </div>
                    <div className="tg-field">
                      <label className="tg-label">SMS number</label>
                      <input className="tg-input" type="tel" value={val('alert_phone')} onChange={e => set('alert_phone', e.target.value)} onBlur={() => blur('alert_phone')} placeholder="+491701234567" style={{ fontFamily: 'var(--tg-font-mono)' }} />
                      {errs.alert_phone && <div className="tg-field-error">{errs.alert_phone}</div>}
                    </div>
                  </div>
                  <div className="frow" style={{ marginTop: 12 }}>
                    <div className="tg-field">
                      <label className="tg-label">WhatsApp number <span style={{ color: 'var(--tg-text-muted)', fontWeight: 400 }}>· optional</span></label>
                      <input className="tg-input" type="tel" value={val('alert_whatsapp')} onChange={e => set('alert_whatsapp', e.target.value)} onBlur={() => blur('alert_whatsapp')} placeholder="Defaults to SMS number" style={{ fontFamily: 'var(--tg-font-mono)' }} />
                      {errs.alert_whatsapp && <div className="tg-field-error">{errs.alert_whatsapp}</div>}
                    </div>
                    <div className="tg-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <TestAlertButton />
                    </div>
                  </div>
                </div>
                <div className="scard__foot">
                  <Button variant="ghost" onClick={discard} disabled={!dirty}>Cancel</Button>
                  <Button variant="primary" onClick={save} loading={saving} disabled={saving || hasErrors || !dirty}>Save changes</Button>
                </div>
              </div>

              <div className="scard">
                <div className="scard__head">
                  <h2>Operating hours</h2>
                  <p>Tankguard alerts when a truck moves outside these hours — joyriding, unauthorised use, or after-hours theft. Times are interpreted in UTC.</p>
                </div>
                <div className="scard__body">
                  <div className="frow">
                    <div className="tg-field">
                      <label className="tg-label">Start time</label>
                      <input className="tg-input" type="time" value={val('operating_hours_start') || '06:00'} onChange={e => set('operating_hours_start', e.target.value)} onBlur={() => blur('operating_hours_start')} style={{ fontFamily: 'var(--tg-font-mono)' }} />
                      {errs.operating_hours_start && <div className="tg-field-error">{errs.operating_hours_start}</div>}
                    </div>
                    <div className="tg-field">
                      <label className="tg-label">End time</label>
                      <input className="tg-input" type="time" value={val('operating_hours_end') || '22:00'} onChange={e => set('operating_hours_end', e.target.value)} onBlur={() => blur('operating_hours_end')} style={{ fontFamily: 'var(--tg-font-mono)' }} />
                      {errs.operating_hours_end && <div className="tg-field-error">{errs.operating_hours_end}</div>}
                    </div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <label className="tg-label" style={{ display: 'block', marginBottom: 8 }}>Operating days</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {DAY_LABELS.map(({ id, label }) => {
                        const cur = val('operating_days') || '1,2,3,4,5'
                        const active = String(cur).split(',').map(s => parseInt(s.trim())).includes(id)
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => set('operating_days', toggleDay(cur, id))}
                            className={active ? 'is-active' : ''}
                            style={{
                              minWidth: 56, padding: '7px 12px', borderRadius: 'var(--tg-radius-md)',
                              border: '1px solid ' + (active ? 'var(--tg-teal-500)' : 'var(--tg-border-default)'),
                              background: active ? 'var(--tg-teal-50, #ecfdf5)' : 'var(--tg-bg-surface)',
                              color: active ? 'var(--tg-teal-700)' : 'var(--tg-text-secondary)',
                              fontSize: 'var(--tg-text-sm)', fontWeight: active ? 600 : 500, cursor: 'pointer',
                            }}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>
                    <div className="tg-help" style={{ marginTop: 8 }}>Trucks moving on days outside this set trip an alert too — useful for catching weekend joyriding.</div>
                  </div>
                  <div style={{ marginTop: 16 }} className="toggle-row">
                    <div>
                      <div className="tl">Alert on movement outside operating hours</div>
                      <div className="ts">When off, the window is recorded but no email/SMS is sent.</div>
                    </div>
                    <Toggle
                      checked={(val('alert_after_hours_movement') ?? 'true') !== 'false'}
                      onChange={v => set('alert_after_hours_movement', v ? 'true' : 'false')}
                    />
                  </div>
                </div>
                <div className="scard__foot">
                  <Button variant="ghost" onClick={discard} disabled={!dirty}>Cancel</Button>
                  <Button variant="primary" onClick={save} loading={saving} disabled={saving || hasErrors || !dirty}>Save changes</Button>
                </div>
              </div>

              <div className="scard">
                <div className="scard__head">
                  <h2>Notifications</h2>
                  <p>Where alerts are delivered. Theft always notifies — channel is configurable.</p>
                </div>
                <div className="scard__body">
                  {[
                    { key: 'email_theft',     label: 'Theft alerts — email',    sub: 'Immediate email to all org admins' },
                    { key: 'sms_theft',       label: 'Theft alerts — SMS',      sub: 'Text the alert number the moment theft is detected' },
                    { key: 'whatsapp_theft',  label: 'Theft alerts — WhatsApp', sub: 'Send to the WhatsApp number (Twilio Business required)' },
                    { key: 'push_theft',      label: 'Theft alerts — push',     sub: 'Push to the Tankguard mobile app' },
                    { key: 'sensor_health',   label: 'Sensor health warnings',  sub: 'Low battery, signal loss, offline > 2h' },
                    { key: 'weekly_summary',  label: 'Weekly summary',          sub: 'Monday digest of fuel + distance' },
                  ].map(({ key, label, sub }) => (
                    <div key={key} className="toggle-row">
                      <div>
                        <div className="tl">{label}</div>
                        <div className="ts">{sub}</div>
                      </div>
                      <Toggle
                        checked={notifValue(key)}
                        onChange={v => setNotifValue(key, v)}
                      />
                    </div>
                  ))}
                </div>
                <div className="scard__foot">
                  <PreviewWeeklyButton />
                  <span style={{ flex: 1 }} />
                  <Button variant="ghost" onClick={discard} disabled={!dirty}>Cancel</Button>
                  <Button variant="primary" onClick={save} loading={saving} disabled={saving || hasErrors || !dirty}>Save changes</Button>
                </div>
              </div>
            </>
          )}

          {section === 'webhooks' && <WebhooksSection />}

          {section === 'team' && <TeamSection />}

          {section === 'devices' && (
            <div className="scard">
              <div className="scard__head">
                <h2>Device provisioning</h2>
                <p>How GPS units and fuel sensors are registered to your fleet.</p>
              </div>
              <div className="scard__body">
                <div className="notice">
                  <span className="ic"><InfoIcon /></span>
                  <span className="nt">
                    <b>Devices are added explicitly.</b> Tankguard never auto-provisions an unknown IMEI. A new GPS unit or fuel sensor only joins your fleet when an admin adds it by IMEI from the Fleet page — so a stray device on the network can never silently appear in your org.
                  </span>
                </div>
                <div className="frow" style={{ marginTop: 20 }}>
                  <div className="tg-field">
                    <label className="tg-label">Data refresh interval</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input className="tg-input" type="number" min="5" max="300" value={val('refresh_interval_s')} onChange={e => set('refresh_interval_s', e.target.value)} onBlur={() => blur('refresh_interval_s')} style={{ width: 88, fontFamily: 'var(--tg-font-mono)' }} />
                      <span style={{ fontSize: 'var(--tg-text-base)', color: 'var(--tg-text-tertiary)' }}>seconds</span>
                    </div>
                    {errs.refresh_interval_s && <div className="tg-field-error" style={{ marginTop: 6 }}>{errs.refresh_interval_s}</div>}
                  </div>
                  <div className="tg-field">
                    <label className="tg-label">Firmware channel</label>
                    <select className="tg-input" style={{ appearance: 'auto' }}>
                      <option>Stable</option>
                      <option>Beta</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="scard__foot">
                <span style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', flex: 1 }}>
                  To add a device, open the Fleet tab and use “Add truck”.
                </span>
                <Button variant="primary" onClick={save} loading={saving} disabled={saving || hasErrors || !dirty}>Save changes</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isSuperadmin && (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--tg-border)' }}>
          <p style={{ fontSize: 13, color: 'var(--tg-graphite-400)', marginBottom: 8 }}>
            Superadmin tools have moved.
          </p>
          <a
            href="https://admin.tankguard.de"
            target="_blank"
            rel="noopener noreferrer"
            className="tg-btn tg-btn--secondary tg-btn--sm"
          >
            Open Admin Panel →
          </a>
        </div>
      )}

      <div className={`savebar${dirty ? ' is-visible' : ''}`}>
        <span className="st">You have unsaved changes</span>
        <Button variant="ghost" size="sm" style={{ color: 'var(--tg-graphite-300)' }} onClick={discard}>Discard</Button>
        <Button variant="primary" size="sm" onClick={save} loading={saving} disabled={saving || hasErrors}>Save</Button>
      </div>
    </div>
  )
}
